import { useAnalyticsEmitter } from '@utils/analytics/emitter';
import Link from 'next/link';
import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { UrlObject } from 'url';

type Props = {
  onClick?: () => void;
  activeTab: string;
  tabs: {
    key: string;
    label: ReactNode;
    href: string | UrlObject;
    hrefAs?: string | UrlObject;
  }[];
};

const Tabs: React.FC<Props> = ({ activeTab, onClick, tabs }) => {
  const [emitter] = useAnalyticsEmitter();

  const [markerPosition, setMarkerPosition] = useState({ left: 0, width: 0 });
  const activeRef = useRef<HTMLAnchorElement>(null);

  useEffect(() => {
    if (activeRef.current === null) return;

    setMarkerPosition({
      left: activeRef.current.offsetLeft,
      width: activeRef.current.clientWidth,
    });
  }, [activeTab, activeRef]);

  return (
    <div className="relative mx-auto">
      <div className="flex items-center justify-start" role="tablist">
        {tabs.map((tab) => (
          <Link
            key={tab.key}
            ref={tab.key === activeTab ? activeRef : null}
            aria-controls={`panel-${tab.key}`}
            aria-selected={tab.key === activeTab}
            as={tab.hrefAs}
            className="my-2 cursor-pointer whitespace-nowrap rounded-sm p-2 text-center outline outline-2 outline-offset-2 outline-transparent transition-all duration-300 ease-out hover:text-neutral-4 focus:outline-neutral-4/50 active:scale-95"
            href={tab.href}
            id={`tab-${tab.key}`}
            onClick={() => {
              emitter('', 'tab clicked', {
                'current tab': activeTab,
                'target tab': tab.key,
              });

              onClick?.();
            }}
            prefetch={false}
            role="tab"
            shallow
          >
            {tab.label}
          </Link>
        ))}
      </div>

      <div
        className="pointer-events-none absolute bottom-0 left-0 h-0.5 w-px bg-neutral-6 transition-all duration-300"
        style={{
          transformOrigin: 'left center',
          transform: `translateX(${markerPosition.left}px) scaleX(${
            markerPosition.width * 100
          }%)`,
        }}
      />
    </div>
  );
};

export default Tabs;
